import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit2, CheckCircle, XCircle, ExternalLink, FileText, Eye, Code, Split, MessageSquare, Shield, Video, AlertTriangle, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { cn } from '@/lib/utils';


interface Tool {
  tool_name: string;
  url: string;
  what_it_does: string;
}

interface FactAnalysisResponse {
  text: string;
  notes: string;
}

interface FactCheckItem {
  Statement: string;
  Yes_Count: number;
  No_Count: number;
  No_Sources?: Array<{
    source: string;
    reason: string;
    suggestion: string;
  }>;
}

interface FactCheckResponse {
  [key: number]: FactCheckItem;
}

interface ToolsResponse {
  tools: Tool[];
}

interface Article {
  article_id: string;
  post_title: string;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'UPDATING';
  created_at: string;
  updated_at?: string;
  article?: any;
  tools?: Array<{ urls: string[] }>;
}

const StartupStashSection: React.FC = () => {
  const [formData, setFormData] = useState({
    postTitle: '',
    focusKeyword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [toolsData, setToolsData] = useState<Tool[]>([]);
  const [isLoadingTools, setIsLoadingTools] = useState(false);
  const [acceptedTools, setAcceptedTools] = useState<Set<string>>(new Set());
  const [discardedTools, setDiscardedTools] = useState<Set<string>>(new Set());
  const [generatedMarkdown, setGeneratedMarkdown] = useState('');
  const [isGeneratingArticle, setIsGeneratingArticle] = useState(false);
  const [markdownViewMode, setMarkdownViewMode] = useState<'split' | 'editor' | 'preview'>('split');
  const [chatInput, setChatInput] = useState('');
  const [isSendingChat, setIsSendingChat] = useState(false);
  const [isAddingImages, setIsAddingImages] = useState(false);
  const [isAnalyzingFacts, setIsAnalyzingFacts] = useState(false);
  const [factAnalysisNotes, setFactAnalysisNotes] = useState('');
  const [isDownloadingGif, setIsDownloadingGif] = useState(false);
  const [downloadCompleted, setDownloadCompleted] = useState(false);
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [isFactChecking, setIsFactChecking] = useState(false);
  const [factCheckResults, setFactCheckResults] = useState<FactCheckItem[]>([]);
  const [selectedFacts, setSelectedFacts] = useState<Set<number>>(new Set());
  const [showFactCheck, setShowFactCheck] = useState(false);
  const [showAllFacts, setShowAllFacts] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoadingArticles, setIsLoadingArticles] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [currentArticleId, setCurrentArticleId] = useState<string | null>(null);
  const [isEditingArticle, setIsEditingArticle] = useState(false);
  const [showSavedTip, setShowSavedTip] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Fetch recent articles
  const fetchArticles = async () => {
    setIsLoadingArticles(true);
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      
      const { data, error } = await supabase.functions.invoke('articles-api', {
        body: { action: 'list' }
      });

      if (error) throw error;
      
      setArticles(data?.articles || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast({
        title: "Error",
        description: "Failed to fetch articles",
        variant: "destructive"
      });
    } finally {
      setIsLoadingArticles(false);
    }
  };

  // Background refresh without showing loading state
  const refreshArticles = async () => {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      
      const { data, error } = await supabase.functions.invoke('articles-api', {
        body: { action: 'list' }
      });

      if (error) throw error;
      
      setArticles(data?.articles || []);
    } catch (error) {
      console.error('Error refreshing articles:', error);
    }
  };

  // Load articles on component mount and set up auto-refresh
  React.useEffect(() => {
    fetchArticles();
    const interval = setInterval(refreshArticles, 30000); // Auto-refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const handleArticleClick = (article: Article) => {
    console.log('Article clicked:', article);
    console.log('Article tools:', article.tools);
    
    if ((article.status === 'COMPLETED' || article.status === 'UPDATING' || article.status === 'FAILED') && article.article) {
      setSelectedArticle(article);
      setCurrentArticleId(article.article_id);
      try {
        const articleContent = typeof article.article === 'string' 
          ? JSON.parse(article.article) 
          : article.article;
        const content = articleContent.text || articleContent.content || articleContent.markdown || '';
        setGeneratedMarkdown(content);
        setFormData({
          postTitle: article.post_title,
          focusKeyword: '' // Reset focus keyword
        });
        
        // Do not auto-load saved tools when opening an article; show only new tools when user clicks 'Find Tools'
        setToolsData([]);
        setAcceptedTools(new Set());
        setDiscardedTools(new Set());
      } catch (error) {
        console.error('Error parsing article content:', error);
        toast({
          title: "Error",
          description: "Failed to load article content",
          variant: "destructive"
        });
      }
    } else {
      console.log('Article not completed or missing content');
    }
  };

  // Step 1 (updated): Find tools first using the new webhook
  const findToolsFirst = async () => {
    if (!formData.postTitle || !formData.focusKeyword) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('https://growtika.app.n8n.cloud/webhook/find-tools', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          post_title: formData.postTitle,
          focus_keyword: formData.focusKeyword
        })
      });

      if (!response.ok) {
        throw new Error('Failed to find tools');
      }

      const data = await response.json();
      const toolsResponse = Array.isArray(data) ? data[0] : data;
      setToolsData(toolsResponse.tools || []);
      setAcceptedTools(new Set());
      setDiscardedTools(new Set());

      toast({
        title: "Success",
        description: "Tools found successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to find tools. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };


  const handleToolAction = (toolName: string, action: 'accept' | 'discard') => {
    if (action === 'accept') {
      setAcceptedTools(prev => new Set([...prev, toolName]));
      setDiscardedTools(prev => {
        const newSet = new Set(prev);
        newSet.delete(toolName);
        return newSet;
      });
    } else {
      setDiscardedTools(prev => new Set([...prev, toolName]));
      setAcceptedTools(prev => {
        const newSet = new Set(prev);
        newSet.delete(toolName);
        return newSet;
      });
    }
  };

  const generateArticle = async () => {
    if (acceptedTools.size === 0) {
      toast({
        title: "Error",
        description: "Please accept at least one tool before generating article",
        variant: "destructive"
      });
      return;
    }

    setIsGeneratingArticle(true);
    try {
      const acceptedToolsWithUrls = toolsData.filter(tool => 
        acceptedTools.has(tool.tool_name)
      );

      const requestBody = {
        post_title: formData.postTitle,
        focus_keyword: formData.focusKeyword,
        accepted_tools: acceptedToolsWithUrls
      };

      // Step 1: Trigger the workflow and get execution_id
      const response = await fetch('https://growtika.app.n8n.cloud/webhook/article-generation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('Error response text:', errorText);
        throw new Error(`Failed to trigger article generation: ${response.status} ${errorText}`);
      }

      // Get response as text first to debug
      const responseText = await response.text();
      console.log('Raw response text:', responseText);
      
      // Try to parse JSON
      let triggerData;
      try {
        triggerData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.error('Response that failed to parse:', responseText);
        throw new Error('Invalid response format from webhook');
      }

      const executionId = triggerData.execution_id;
      
      if (!executionId) {
        throw new Error('No execution ID received from workflow');
      }

      setCurrentArticleId(executionId);

      console.log('Article generation triggered with execution_id:', executionId);
      
      toast({
        title: "Article Generation Started",
        description: "Your article is being generated. This may take a few minutes..."
      });

      // Step 2: Start client-side polling
      await pollForArticle(executionId);

    } catch (error) {
      console.error('Article generation error:', error);
      toast({
        title: "Error", 
        description: error instanceof Error ? error.message : "Failed to generate article. Please try again.",
        variant: "destructive"
      });
      setIsGeneratingArticle(false);
    }
  };

  const pollForArticle = async (executionId: string) => {
    const maxPollingTime = 10 * 60 * 1000; // 10 minutes
    const pollingInterval = 15 * 1000; // 15 seconds
    const startTime = Date.now();

    // Wait 1 minute before starting polls
    await new Promise(resolve => setTimeout(resolve, 60 * 1000));

    const poll = async (): Promise<void> => {
      try {
        console.log(`Polling for execution_id: ${executionId}`);
        
        const pollingResponse = await fetch('https://zbponitofdhxisnyhnfd.supabase.co/functions/v1/article-polling', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpicG9uaXRvZmRoeGlzbnlobmZkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDIwNDk5MTQsImV4cCI6MjA1NzYyNTkxNH0.Fndqg98ar6tF5wz1DUxNB3nfVTnLkPNmSqM4eWFkmwo`
          },
          body: JSON.stringify({ execution_id: executionId })
        });

        if (pollingResponse.ok) {
          const pollingData = await pollingResponse.json();
          
          if (pollingData.success && pollingData.article) {
            // Article found!
            setGeneratedMarkdown(pollingData.article);
            setIsGeneratingArticle(false);
            toast({
              title: "Success",
              description: "Article generated successfully!"
            });
            return;
          }
        }

        // Check if we've exceeded the timeout
        if (Date.now() - startTime >= maxPollingTime) {
          throw new Error('Article generation timeout - please try again');
        }

        // Continue polling
        setTimeout(poll, pollingInterval);

      } catch (error) {
        console.error('Polling error:', error);
        setIsGeneratingArticle(false);
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to retrieve article. Please try again.",
          variant: "destructive"
        });
      }
    };

    // Start the polling loop
    poll();
  };

  // New function for editing articles with proper polling
  const editArticle = async () => {
    if (!chatInput.trim()) {
      toast({
        title: "Error",
        description: "Please enter an edit request",
        variant: "destructive"
      });
      return;
    }

    if (!generatedMarkdown) {
      toast({
        title: "Error",
        description: "No article available to edit",
        variant: "destructive"
      });
      return;
    }

    setIsEditingArticle(true);
    
    try {
      const requestBody = {
        post_title: formData.postTitle,
        article_id: selectedArticle?.article_id || currentArticleId || null,
        chat_query: chatInput,
        article: generatedMarkdown,
        web_search: useWebSearch
      };

      // Step 1: Trigger the edit workflow and get execution_id
      const response = await fetch('https://growtika.app.n8n.cloud/webhook/article-generation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Edit response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('Edit error response text:', errorText);
        throw new Error(`Failed to trigger article edit: ${response.status} ${errorText}`);
      }

      // Get response as text first to debug
      const responseText = await response.text();
      console.log('Edit raw response text:', responseText);
      
      // Try to parse JSON
      let triggerData;
      try {
        triggerData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Edit JSON parse error:', parseError);
        console.error('Edit response that failed to parse:', responseText);
        throw new Error('Invalid response format from edit webhook');
      }

      const executionId = triggerData.execution_id;
      
      if (!executionId) {
        throw new Error('No execution ID received from edit workflow');
      }

      console.log('Article edit triggered with execution_id:', executionId);
      
      toast({
        title: "Article Edit Started",
        description: "Your article is being edited. This may take a few minutes..."
      });

      // Step 2: Start client-side polling for the edited article
      await pollForEditedArticle(executionId);

    } catch (error) {
      console.error('Article edit error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to edit article. Please try again.",
        variant: "destructive"
      });
      setIsEditingArticle(false);
    }
  };

  const pollForEditedArticle = async (executionId: string) => {
    const maxPollingTime = 10 * 60 * 1000; // 10 minutes
    const pollingInterval = 10 * 1000; // 10 seconds
    const startTime = Date.now();

    // Wait 1 minute before starting polls
    await new Promise(resolve => setTimeout(resolve, 60 * 1000));

    const poll = async (): Promise<void> => {
      try {
        console.log(`Polling for edited article execution_id: ${executionId}`);
        
        const pollingResponse = await fetch('https://zbponitofdhxisnyhnfd.supabase.co/functions/v1/article-polling', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpicG9uaXRvZmRoeGlzbnlobmZkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDIwNDk5MTQsImV4cCI6MjA1NzYyNTkxNH0.Fndqg98ar6tF5wz1DUxNB3nfVTnLkPNmSqM4eWFkmwo`
          },
          body: JSON.stringify({ execution_id: executionId })
        });

         if (pollingResponse.ok) {
           const pollingData = await pollingResponse.json();
           console.log('Edit polling response:', pollingData);
           
           // Only stop when status is COMPLETED
           if (pollingData.status === 'COMPLETED') {
             console.log('Edit status COMPLETED, fetching article from DB and stopping polling');
             try {
               const { supabase } = await import('@/integrations/supabase/client');
               const { data, error } = await supabase.functions.invoke('articles-api', {
                 body: { action: 'list' }
               });
               if (error) throw error;
               const updatedList = data?.articles || [];
               setArticles(updatedList);
               const updated = updatedList.find((a: any) => a.article_id === (selectedArticle?.article_id || executionId));
               if (updated && updated.article) {
                 const parsed = typeof updated.article === 'string' ? JSON.parse(updated.article) : updated.article;
                 const content = parsed.text || parsed.content || parsed.markdown || '';
                 setGeneratedMarkdown(content);
                 setSelectedArticle(updated);
               }
               setChatInput('');
               setIsEditingArticle(false);
               
               toast({
                 title: "Article Updated",
                 description: "Your article has been successfully updated."
               });
               return;
             } catch (fetchErr) {
               console.error('Failed to fetch updated article from DB:', fetchErr);
               // Fallback: if API list fails but polling returned article, update from polling
               if (pollingData.article) {
                 setGeneratedMarkdown(pollingData.article);
               }
               setIsEditingArticle(false);
               toast({ title: 'Article Updated', description: 'Content refreshed.' });
               return;
             }
           }
         } else {
           console.log('Edit polling response not ok:', pollingResponse.status);
         }

        // Check if we've exceeded the timeout
        if (Date.now() - startTime >= maxPollingTime) {
          // Mark status as UPDATE_FAILED and stop polling
          setIsEditingArticle(false);
          
          // Try to update the article status to UPDATE_FAILED
          try {
            const { supabase } = await import('@/integrations/supabase/client');
            if (selectedArticle?.article_id) {
              await supabase.functions.invoke('articles-api', {
                method: 'PUT',
                body: { 
                  article_id: selectedArticle.article_id,
                  status: 'UPDATE_FAILED'
                }
              });
              refreshArticles();
            }
          } catch (updateError) {
            console.error('Error updating article status:', updateError);
          }
          
          toast({
            title: "Update Timeout",
            description: "Article update timed out after 8 minutes. Status marked as failed.",
            variant: "destructive"
          });
          return;
        }

        // Continue polling
        setTimeout(poll, pollingInterval);

      } catch (error) {
        console.error('Edit polling error:', error);
        setIsEditingArticle(false);
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to retrieve edited article. Please try again.",
          variant: "destructive"
        });
      }
    };

    // Start the polling loop
    poll();
  };


  const runFactCheck = async () => {
    if (!generatedMarkdown) {
      toast({
        title: "Error",
        description: "No article content available for fact-checking",
        variant: "destructive"
      });
      return;
    }

    setIsFactChecking(true);
    try {
      const response = await fetch('https://growtika.app.n8n.cloud/webhook/file-upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: generatedMarkdown
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fact-check article');
      }

      const data = await response.json();
      console.log('Fact-check response:', data);
      
      // Convert response object to array and store all facts
      const factsArray = Object.values(data) as FactCheckItem[];
      setFactCheckResults(factsArray);
      
      // Filter out bad facts (disputed facts with No_Count > 0)
      const badFacts = factsArray.filter(fact => 
        fact.No_Count > 0 && fact.No_Sources && fact.No_Sources.length > 0
      );
      
      // Auto-select all bad facts by default
      const badFactIndices = new Set(
        factsArray
          .map((fact, index) => 
            fact.No_Count > 0 && fact.No_Sources && fact.No_Sources.length > 0 ? index : -1
          )
          .filter(index => index !== -1)
      );
      setSelectedFacts(badFactIndices);
      
      // If there are bad facts, automatically put them in the edit box
      if (badFacts.length > 0) {
        const factFixesText = badFacts
          .map(fact => {
            const suggestions = fact.No_Sources?.map(src => src.suggestion).join(' ') || '';
            return `Fix this statement: "${fact.Statement}". Suggested improvements: ${suggestions}`;
          })
          .join('\n\n');
        
        setChatInput(`Please fix the following fact-checking issues in the article:\n\n${factFixesText}`);
      }
      
      setShowFactCheck(true);

      toast({
        title: "Fact-Check Complete",
        description: badFacts.length > 0 
          ? `Found ${badFacts.length} disputed facts that need fixing` 
          : "All facts verified successfully!"
      });
    } catch (error) {
      console.error('Fact-check error:', error);
      toast({
        title: "Error",
        description: "Failed to fact-check article. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsFactChecking(false);
    }
  };

  const toggleFactSelection = (index: number) => {
    setSelectedFacts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const applyFactFixes = async () => {
    if (selectedFacts.size === 0) {
      toast({
        title: "Error",
        description: "Please select at least one fact to fix",
        variant: "destructive"
      });
      return;
    }

    const selectedFactItems = Array.from(selectedFacts).map(index => factCheckResults[index]);
    const factFixesText = selectedFactItems
      .map(fact => {
        const suggestions = fact.No_Sources?.map(src => src.suggestion).join(' ') || '';
        return `Fix this statement: "${fact.Statement}". Suggested improvements: ${suggestions}`;
      })
      .join('\n\n');

    // Set the chat input with fact fixes and trigger edit
    setChatInput(`Please fix the following fact-checking issues in the article:\n\n${factFixesText}`);
    
    // Hide fact-check panel
    setShowFactCheck(false);
    
    // Auto-trigger the edit
    setIsSendingChat(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      const response = await fetch('https://growtika.app.n8n.cloud/webhook/article-generation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_query: `Please fix the following fact-checking issues in the article:\n\n${factFixesText}`,
          article: generatedMarkdown,
          web_search: useWebSearch
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to apply fact fixes');
      }

      const data = await response.json();
      console.log('Fact fix response:', data);

      let markdown;
      if (data.text && data.text.value) {
        markdown = data.text.value;
      } else if (Array.isArray(data) && data.length > 0 && data[0].text) {
        if (data[0].text.value) {
          markdown = data[0].text.value;
        } else {
          markdown = data[0].text;
        }
      } else {
        markdown = data.article || data.content || data.markdown || data.text;
      }
      
      if (markdown) {
        setGeneratedMarkdown(markdown);
        setChatInput('');
        
        toast({
          title: "Fact Fixes Applied",
          description: "Article has been updated with fact corrections."
        });
      } else {
        throw new Error('No edited content received');
      }
    } catch (error) {
      console.error('Fact fix error:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        toast({
          title: "Timeout", 
          description: "Fact fixing took too long. Please try again.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error", 
          description: error instanceof Error ? error.message : "Failed to apply fact fixes. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setIsSendingChat(false);
    }
  };

  const getFactStatusIcon = (fact: FactCheckItem) => {
    if (fact.Yes_Count === 3) return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (fact.Yes_Count === 0 && fact.No_Count > 0) return <XCircle className="h-4 w-4 text-red-600" />;
    if (fact.Yes_Count > 0 && fact.No_Count > 0) return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    if (fact.Yes_Count > 0 && fact.No_Count === 0) return <CheckCircle className="h-4 w-4 text-green-600" />;
    return <Clock className="h-4 w-4 text-muted-foreground" />;
  };

  const getFactStatusText = (fact: FactCheckItem) => {
    if (fact.Yes_Count === 3) return 'Verified';
    if (fact.Yes_Count === 0 && fact.No_Count > 0) return 'Disputed';
    if (fact.Yes_Count > 0 && fact.No_Count > 0) return 'Mixed Evidence';
    if (fact.Yes_Count > 0 && fact.No_Count === 0) return 'Verified';
    return 'Pending Review';
  };

  const getFactCardClass = (fact: FactCheckItem) => {
    if (fact.Yes_Count === 3) return 'border-green-200 bg-green-50/50';
    if (fact.Yes_Count === 0 && fact.No_Count > 0) return 'border-red-200 bg-red-50/50';
    if (fact.Yes_Count > 0 && fact.No_Count > 0) return 'border-yellow-200 bg-yellow-50/50';
    if (fact.Yes_Count > 0 && fact.No_Count === 0) return 'border-green-200 bg-green-50/50';
    return 'border-muted bg-muted/20';
  };


  const sendChatQuery = async () => {
    if (!chatInput.trim()) {
      toast({
        title: "Error",
        description: "Please enter a message",
        variant: "destructive"
      });
      return;
    }

    setIsSendingChat(true);
    try {
      const response = await fetch('https://growtika.app.n8n.cloud/webhook/find-tools', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tools: toolsData,
          chat_query: chatInput
        })
      });

      if (!response.ok) {
        throw new Error('Failed to process chat query');
      }

      const data = await response.json();
      const toolsResponse = Array.isArray(data) ? data[0] : data;
      setToolsData(toolsResponse.tools || []);
      setAcceptedTools(new Set());
      setDiscardedTools(new Set());
      setChatInput('');

      toast({
        title: "Success",
        description: "Tools updated based on your request!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to process chat query. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSendingChat(false);
    }
  };


  const handleAddImages = async () => {
    if (!generatedMarkdown) {
      toast({
        title: "Error",
        description: "No article content available",
        variant: "destructive"
      });
      return;
    }

    // Extract URLs from DB tools (nested structure) or fallback to accepted tools
    let toolUrls: string[] = [];
    
    // Handle nested tools structure from DB: [{"urls": ["url1", "url2"]}]
    if (selectedArticle?.tools && selectedArticle.tools.length > 0) {
      try {
        const toolsData = selectedArticle.tools[0];
        if (toolsData && toolsData.urls && Array.isArray(toolsData.urls)) {
          toolUrls = toolsData.urls;
        }
      } catch (error) {
        console.error('Error parsing tools from article:', error);
      }
    }
    
    // Fallback to accepted tools if no DB tools found
    if (toolUrls.length === 0 && acceptedTools.size === 0) {
      toast({
        title: "Error",
        description: "No tools available",
        variant: "destructive"
      });
      return;
    }

    setIsAddingImages(true);
    try {
      if (toolUrls.length === 0) {
        const acceptedToolsWithUrls = toolsData.filter(tool => 
          acceptedTools.has(tool.tool_name)
        );
        toolUrls = acceptedToolsWithUrls.map(tool => tool.url);
      }

      const requestBody = {
        urls: toolUrls,
        markdown: generatedMarkdown
      };
      console.log('Sending to add screenshots:', requestBody); // Debug log

      const response = await fetch('https://growtika.app.n8n.cloud/webhook/add-screenshots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to add screenshots');
      }

      const data = await response.json();
      console.log('Add screenshots response:', data); // Debug log

      // Handle the response same as generate-article
      if (Array.isArray(data) && data.length > 0 && data[0].text) {
        // Clean up the text by removing extra newlines at start and code blocks at end
        let cleanedText = data[0].text.trim();
        if (cleanedText.endsWith('```')) {
          cleanedText = cleanedText.slice(0, -3).trim();
        }
        
        setGeneratedMarkdown(cleanedText);
        toast({
          title: "Success",
          description: "Screenshots added successfully! Article updated with images."
        });
      } else {
        toast({
          title: "Success", 
          description: "Screenshots request sent successfully!"
        });
      }
    } catch (error) {
      console.error('Add screenshots error:', error);
      toast({
        title: "Error", 
        description: error instanceof Error ? error.message : "Failed to add screenshots. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsAddingImages(false);
    }
  };

  const analyzeAndImproveContent = async () => {
    if (!generatedMarkdown) {
      toast({
        title: "Error",
        description: "No article content available to analyze",
        variant: "destructive"
      });
      return;
    }

    setIsAnalyzingFacts(true);
    try {
      const response = await fetch('https://growtika.app.n8n.cloud/webhook/article-generation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          old_article: generatedMarkdown
        })
      });

      if (!response.ok) {
        throw new Error('Failed to analyze facts');
      }

      const data: FactAnalysisResponse[] = await response.json();
      
      if (Array.isArray(data) && data.length > 0) {
        const analysisResult = data[0];
        setGeneratedMarkdown(analysisResult.text);
        setFactAnalysisNotes(analysisResult.notes);
        
        toast({
          title: "Success",
          description: "Facts analyzed successfully! Content updated."
        });
      } else {
        throw new Error('No analysis data received');
      }
    } catch (error) {
      console.error('Fact analysis error:', error);
      toast({
        title: "Error", 
        description: error instanceof Error ? error.message : "Failed to analyze facts. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzingFacts(false);
    }
  };

  const handleSaveArticle = async () => {
    console.log('Save article clicked');
    if (!selectedArticle || !generatedMarkdown.trim()) {
      console.log('No article or content to save');
      toast({
        title: "Error",
        description: "No content to save",
        variant: "destructive"
      });
      return;
    }

    try {
      console.log('Starting save process...');
      const { supabase } = await import('@/integrations/supabase/client');
      
      const updatedArticle = {
        text: generatedMarkdown
      };

      console.log('Calling articles-api with:', {
        article_id: selectedArticle.article_id,
        article: JSON.stringify(updatedArticle),
        updated_at: new Date().toISOString()
      });

      const { data, error } = await supabase.functions.invoke('articles-api', {
        body: {
          article_id: selectedArticle.article_id,
          article: JSON.stringify(updatedArticle),
          updated_at: new Date().toISOString()
        }
      });

      console.log('Save response:', { data, error });

      if (error) {
        console.error('Save error:', error);
        throw error;
      }

      console.log('Save successful, showing toast...');
      setShowSavedTip(true);
      setTimeout(() => setShowSavedTip(false), 2000);

      toast({
        title: "✅ Article Saved",
        description: "Your changes have been saved successfully",
        duration: 3000
      });

      console.log('Toast called');
      
      // Update the selected article with new content
      setSelectedArticle({
        ...selectedArticle,
        article: JSON.stringify(updatedArticle),
        updated_at: new Date().toISOString()
      });

      console.log('Refreshing articles...');
      // Refresh the articles list
      await fetchArticles();
      console.log('Articles refreshed');
    } catch (error) {
      console.error('Error saving article:', error);
      toast({
        title: "Error",
        description: "Failed to save article. Please try again.",
        variant: "destructive"
      });
    }
  };

  const downloadToolScrollsGIF = async () => {
    console.log('downloadToolScrollsGIF called');
    console.log('selectedArticle:', selectedArticle);
    console.log('acceptedTools:', acceptedTools);
    console.log('toolsData:', toolsData);
    
    // Extract URLs from DB tools (nested structure) or fallback to accepted tools
    let toolUrls: string[] = [];
    
    // Handle nested tools structure from DB: [{"urls": ["url1", "url2"]}]
    if (selectedArticle?.tools && selectedArticle.tools.length > 0) {
      try {
        console.log('Processing selectedArticle tools:', selectedArticle.tools);
        const toolsData = selectedArticle.tools[0];
        console.log('Extracted tools data:', toolsData);
        
        if (toolsData && toolsData.urls && Array.isArray(toolsData.urls)) {
          toolUrls = toolsData.urls;
          console.log('URLs from article tools:', toolUrls);
        }
      } catch (error) {
        console.error('Error parsing tools from article:', error);
      }
    }
    
    // Fallback to accepted tools if no DB tools found
    if (toolUrls.length === 0 && acceptedTools.size === 0) {
      console.log('No tools available - showing error');
      toast({
        title: "Error",
        description: "Please select at least one tool or open an article with saved tools",
        variant: "destructive",
      });
      return;
    }
    
    if (toolUrls.length === 0) {
      const acceptedToolsWithUrls = toolsData.filter(tool => 
        acceptedTools.has(tool.tool_name)
      );
      toolUrls = acceptedToolsWithUrls.map(tool => tool.url);
    }
    
    setIsDownloadingGif(true);
    setDownloadCompleted(false);
    
    // Show loading toast
    toast({
      title: "Processing",
      description: "Generating tool showcase GIF... This may take a moment.",
    });

    try {
      const response = await fetch('https://growtika.app.n8n.cloud/webhook/add-screenshots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toolUrls)
      });

      if (response.ok) {
        // Get the exact zip file as blob
        const blob = await response.blob();
        
        // Use article title for filename or extract from response headers
        let filename = `${formData.postTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.zip`;
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            const serverFilename = filenameMatch[1].replace(/['"]/g, '');
            // Keep the article title but preserve extension from server
            const extension = serverFilename.split('.').pop() || 'zip';
            filename = `${formData.postTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${extension}`;
          }
        }
        
        // Create download link for the exact zip file
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setDownloadCompleted(true);
        
        toast({
          title: "Success",
          description: "Tool showcase GIF package downloaded successfully!",
        });
        
        // Reset download completed state after 3 seconds
        setTimeout(() => {
          setDownloadCompleted(false);
        }, 3000);
      } else {
        throw new Error('Failed to generate GIF package');
      }
    } catch (error) {
      console.error('Error downloading tool showcase:', error);
      toast({
        title: "Error",
        description: "Failed to generate and download tool showcase package",
        variant: "destructive",
      });
    } finally {
      setIsDownloadingGif(false);
    }
  };

  return (
    <div className="w-full min-h-screen pb-24">
      {/* Download Completion Bar */}
      {downloadCompleted && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-0 left-0 right-0 z-50 bg-green-500 text-white py-2 px-4 shadow-lg"
        >
          <div className="container mx-auto flex items-center justify-center gap-2">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Tool showcase package downloaded successfully!</span>
          </div>
        </motion.div>
      )}
      
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Edit2 className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Startup Stash Listicle Automation</h1>
        </div>
        <p className="text-muted-foreground">
          Generate and customize table of contents for your listicle posts
        </p>
      </div>

      <div className="space-y-8">
        {/* Recent Articles Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Recent Articles
              </div>
              <Button
                onClick={refreshArticles}
                disabled={isLoadingArticles}
                variant="outline"
                size="sm"
              >
                Refresh
              </Button>
            </CardTitle>
            <CardDescription>
              Latest 5 articles. Click on completed articles to edit them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingArticles ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No articles found. Create your first article below!
              </div>
            ) : (
              <div className="space-y-3">
                {articles
                  .sort((a, b) => {
                    // Sort by updated_at if available, otherwise by created_at (newest first)
                    const dateA = new Date(a.updated_at || a.created_at).getTime();
                    const dateB = new Date(b.updated_at || b.created_at).getTime();
                    return dateB - dateA;
                  })
                  .map((article, index) => (
                  <div
                    key={article.article_id}
                    className={cn(
                      "flex items-center justify-between p-4 border rounded-lg transition-all duration-200",
                      (article.status === 'COMPLETED' || article.status === 'UPDATING' || article.status === 'FAILED') && "hover:bg-muted/50 cursor-pointer",
                      article.status === 'PROCESSING' && "bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800",
                      article.status === 'UPDATING' && "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800",
                      article.status === 'FAILED' && "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800",
                      selectedArticle?.article_id === article.article_id && "ring-2 ring-primary"
                    )}
                    onClick={() => handleArticleClick(article)}
                  >
                     <div className="flex-1 space-y-1">
                       <div className="flex items-center gap-2">
                         <h3 className="font-medium text-foreground">{article.post_title}</h3>
                         {article.status === 'COMPLETED' && (
                           <CheckCircle className="h-4 w-4 text-green-600" />
                         )}
                          {article.status === 'PROCESSING' && (
                            <Clock className="h-4 w-4 text-yellow-600 animate-pulse" />
                          )}
                          {article.status === 'UPDATING' && (
                            <Clock className="h-4 w-4 text-blue-600 animate-pulse" />
                          )}
                          {article.status === 'FAILED' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {article.updated_at && article.updated_at !== article.created_at && (
                            <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full border border-primary/20">
                              UPDATED
                            </span>
                          )}
                       </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className={cn(
                          "px-2 py-1 rounded-full text-xs font-medium",
                          article.status === 'COMPLETED' && "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200",
                           article.status === 'PROCESSING' && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200",
                           article.status === 'UPDATING' && "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200",
                           article.status === 'FAILED' && "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200"
                        )}>
                          {article.status}
                        </span>
                         <span>Created: {new Date(article.created_at).toLocaleString('en-US', { timeZone: 'Europe/Athens', hour12: true })}</span>
                         {article.updated_at && article.updated_at !== article.created_at && (
                           <span className="text-primary font-medium">
                             Updated: {new Date(article.updated_at).toLocaleString('en-US', { timeZone: 'Europe/Athens', hour12: true })}
                           </span>
                         )}
                         {article.status === 'FAILED' && (
                           <span className="text-red-600 font-mono text-xs">
                             ID: {article.article_id}
                           </span>
                         )}
                      </div>
                       {article.status === 'PROCESSING' && (
                         <p className="text-sm text-yellow-700 dark:text-yellow-300">
                           Processing... should be completed soon
                         </p>
                       )}
                       {article.status === 'UPDATING' && (
                         <p className="text-sm text-blue-700 dark:text-blue-300">
                           Updating... should be completed soon
                         </p>
                       )}
                       {article.status === 'FAILED' && (
                         <p className="text-sm text-red-700 dark:text-red-300">
                           Processing failed. Please check with support using the ID above.
                         </p>
                       )}
                    </div>
                    {article.status === 'COMPLETED' && (
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        {/* Input Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="postTitle">Post Title *</Label>
            <Input
              id="postTitle"
              placeholder="e.g., Best LinkedIn Tools"
              value={formData.postTitle}
              onChange={(e) => handleInputChange('postTitle', e.target.value)}
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="focusKeyword">Focus Keyword *</Label>
            <Input
              id="focusKeyword"
              placeholder="e.g., linkedin tools"
              value={formData.focusKeyword}
              onChange={(e) => handleInputChange('focusKeyword', e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        <Button onClick={findToolsFirst} disabled={isLoading} className="w-full">
          {isLoading ? 'Finding Tools...' : 'Find Tools'}
        </Button>

        {/* Tools Recommendations */}
        {toolsData.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Recommended Tools</h3>
              <div className="text-sm text-muted-foreground">
                Accepted: {acceptedTools.size} | Discarded: {discardedTools.size}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid gap-4"
            >
              {toolsData.map((tool, index) => {
                const isAccepted = acceptedTools.has(tool.tool_name);
                const isDiscarded = discardedTools.has(tool.tool_name);
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className={`rounded-lg p-4 transition-all ${
                      isAccepted ? 'border-2 border-green-500 bg-green-50/50 dark:bg-green-950/20' :
                      isDiscarded ? 'border-2 border-red-500 bg-red-50/50 dark:bg-red-950/20' :
                      'border border-border hover:border-primary/60 bg-card'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{tool.tool_name}</h4>
                          <a 
                            href={tool.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary/80"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </div>
                        <p className="text-sm text-muted-foreground">{tool.what_it_does}</p>
                        <p className="text-xs text-muted-foreground">{tool.url}</p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={isAccepted ? "default" : "outline"}
                          onClick={() => handleToolAction(tool.tool_name, 'accept')}
                          className={isAccepted ? "bg-green-600 hover:bg-green-700" : ""}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant={isDiscarded ? "destructive" : "outline"}
                          onClick={() => handleToolAction(tool.tool_name, 'discard')}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Discard
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Chat Interface for Tool Management */}
            <div className="space-y-4">
              <div className="border rounded-lg p-4 bg-muted/20">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Tool Management Chat
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Ask to add, remove, or modify tools. Example: "Can you add Reco.AI?" or "Remove expensive tools"
                </p>
                <div className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Type your request here..."
                    onKeyPress={(e) => e.key === 'Enter' && sendChatQuery()}
                    className="flex-1"
                  />
                  <Button 
                    onClick={sendChatQuery} 
                    disabled={isSendingChat || !chatInput.trim()}
                    size="sm"
                  >
                    {isSendingChat ? 'Sending...' : 'Send'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Summary of accepted tools */}
            {acceptedTools.size > 0 && (
              <div className="space-y-4">
                <div className="border rounded-lg p-4 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                  <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                    Accepted Tools ({acceptedTools.size})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {Array.from(acceptedTools).map((toolName) => (
                      <span
                        key={toolName}
                        className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded text-sm"
                      >
                        {toolName}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Generate Article Section */}
            {acceptedTools.size > 0 && (
              <div className="flex flex-col items-center space-y-4">
                <Button onClick={generateArticle} disabled={isGeneratingArticle} size="lg">
                  <FileText className="h-4 w-4 mr-2" />
                  {isGeneratingArticle ? 'Generating Article...' : 'Generate Article'}
                </Button>
                
                {/* Engaging Loader with Health Tips */}
                {isGeneratingArticle && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-md mx-auto text-center"
                  >
                    <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-6 border border-primary/20">
                      <div className="flex items-center justify-center mb-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                      </div>
                      <h3 className="font-semibold text-lg mb-2 text-primary">
                        Crafting Your Article ✨
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Your article will appear automatically when ready. Feel free to continue with other work - no need to wait here!
                      </p>
                      
                      {/* Health Tips Carousel */}
                      <motion.div
                        key={Math.floor(Date.now() / 15000)} // Change every 15 seconds
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        className="bg-secondary/30 rounded-md p-3 text-xs"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-green-500">💡</span>
                          <span className="font-medium text-green-700 dark:text-green-400">Health Tip</span>
                        </div>
                        <motion.p 
                          className="text-foreground/80"
                          animate={{ opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          {[
                            "Take a moment to stretch your neck and shoulders",
                            "Look away from your screen and focus on something 20 feet away",
                            "Take 3 deep breaths and relax your jaw muscles",
                            "Roll your shoulders back and check your posture",
                            "Hydrate! Grab a glass of water while you wait",
                            "Blink slowly 10 times to refresh your eyes"
                          ][Math.floor(Date.now() / 15000) % 6]}
                        </motion.p>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </div>
            )}

          </div>
        )}

        {/* Article Editor */}
        {generatedMarkdown && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
              <h3 className="text-lg font-semibold">Generated Article</h3>
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip open={showSavedTip}>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={handleSaveArticle}
                        disabled={!selectedArticle || !generatedMarkdown.trim()}
                        className="bg-primary hover:bg-primary/90"
                        size="sm"
                      >
                        Save Article
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      Article saved
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Button
                  onClick={downloadToolScrollsGIF}
                  variant="outline"
                  size="sm"
                  disabled={isDownloadingGif}
                  className={downloadCompleted ? "border-green-500 bg-green-50 text-green-700" : ""}
                >
                  <Video className="h-4 w-4 mr-1" />
                  {isDownloadingGif ? "Downloading..." : downloadCompleted ? "Downloaded!" : "Tool Showcase GIF"}
                </Button>
              </div>
            </div>

            {/* Fact Analysis Notes */}
            {factAnalysisNotes && (
              <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-medium text-blue-800 dark:text-blue-200">Fact Analysis Notes:</p>
                    <div className="text-sm text-blue-700 dark:text-blue-300 whitespace-pre-wrap">{factAnalysisNotes}</div>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button
                  variant={markdownViewMode === 'split' ? "default" : "outline"}
                  size="sm"
                  onClick={() => setMarkdownViewMode('split')}
                >
                  <Split className="h-4 w-4 mr-1" />
                  Split
                </Button>
                <Button
                  variant={markdownViewMode === 'editor' ? "default" : "outline"}
                  size="sm"
                  onClick={() => setMarkdownViewMode('editor')}
                >
                  <Code className="h-4 w-4 mr-1" />
                  Editor
                </Button>
                <Button
                  variant={markdownViewMode === 'preview' ? "default" : "outline"}
                  size="sm"
                  onClick={() => setMarkdownViewMode('preview')}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </Button>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`grid gap-6 min-h-[600px] ${
                markdownViewMode === 'split' ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"
              }`}
            >
              {(markdownViewMode === 'editor' || markdownViewMode === 'split') && (
                <div className="space-y-2">
                  <Label>Markdown Editor</Label>
                  <Textarea
                    value={generatedMarkdown}
                    onChange={(e) => setGeneratedMarkdown(e.target.value)}
                    className="font-mono text-sm h-full resize-none min-h-[600px]"
                    placeholder="Generated markdown will appear here..."
                  />
                </div>
              )}

              {(markdownViewMode === 'preview' || markdownViewMode === 'split') && (
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div className="border rounded-lg p-4 h-full overflow-auto bg-background min-h-[600px]">
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ children }) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-xl font-semibold mb-3 mt-6">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-lg font-medium mb-2 mt-4">{children}</h3>,
                          p: ({ children }) => <p className="mb-3 text-sm leading-relaxed">{children}</p>,
                          ul: ({ children }) => <ul className="mb-3 space-y-1 pl-4">{children}</ul>,
                          ol: ({ children }) => <ol className="mb-3 space-y-1 pl-4">{children}</ol>,
                          li: ({ children, node }) => {
                            const isNestedList = node?.children?.some((child: any) => 
                              child.type === 'element' && (child.tagName === 'ul' || child.tagName === 'ol')
                            );
                            return (
                              <li className={`text-sm relative ${isNestedList ? 'mb-2' : ''}`}>
                                <span className="flex items-start">
                                  <span className="inline-block w-2 h-2 bg-primary rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                  <span className="flex-1">{children}</span>
                                </span>
                              </li>
                            );
                          },
                          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                          em: ({ children }) => <em className="italic">{children}</em>,
                          a: ({ href, children }) => (
                            <a 
                              href={href} 
                              className="text-primary hover:underline" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                // Prevent React Router from intercepting external links
                                e.stopPropagation();
                                if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
                                  window.open(href, '_blank', 'noopener,noreferrer');
                                  e.preventDefault();
                                }
                              }}
                            >
                              {children}
                            </a>
                          ),
                          table: ({ children }) => (
                            <div className="my-4 overflow-x-auto">
                              <Table className="w-full">
                                {children}
                              </Table>
                            </div>
                          ),
                          thead: ({ children }) => <TableHeader>{children}</TableHeader>,
                          tbody: ({ children }) => <TableBody>{children}</TableBody>,
                          tr: ({ children }) => <TableRow>{children}</TableRow>,
                          th: ({ children }) => <TableHead className="text-left font-medium">{children}</TableHead>,
                          td: ({ children }) => <TableCell>{children}</TableCell>
                        }}
                      >
                        {generatedMarkdown}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
                )}
              </motion.div>


              {/* Fact-Check Interface */}
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-950/20 dark:to-orange-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-yellow-600" />
                    <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200">Fact-Check Article</h3>
                  </div>
                  <Button
                    onClick={runFactCheck}
                    disabled={isFactChecking || !generatedMarkdown}
                    variant="outline"
                    className="bg-yellow-100 border-yellow-300 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-200"
                  >
                    {isFactChecking ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-600 border-t-transparent mr-2" />
                        Fact-Checking...
                      </>
                    ) : (
                      <>
                        <Shield className="h-4 w-4 mr-2" />
                        Run Fact-Check
                      </>
                    )}
                  </Button>
                </div>

                {/* Fact-Check Results */}
                {showFactCheck && factCheckResults.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4"
                  >
                    {(() => {
                      // Filter facts based on toggle
                      const filteredFacts = showAllFacts ? factCheckResults : factCheckResults.filter(fact => 
                        fact.No_Count > 0 && fact.No_Sources && fact.No_Sources.length > 0
                      );
                      
                      const disputedCount = factCheckResults.filter(fact => 
                        fact.No_Count > 0 && fact.No_Sources && fact.No_Sources.length > 0
                      ).length;
                      
                      return (
                        <>
                          <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="h-5 w-5 text-blue-600" />
                              <span className="font-medium text-blue-800 dark:text-blue-200">
                                {showAllFacts 
                                  ? `Showing all ${factCheckResults.length} facts (${disputedCount} disputed)`
                                  : `Found ${disputedCount} disputed facts that need fixing`
                                }
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => setShowAllFacts(!showAllFacts)}
                                variant="outline"
                                size="sm"
                              >
                                {showAllFacts ? 'Show Disputed Only' : 'Show All Facts'}
                              </Button>
                              <Button
                                onClick={applyFactFixes}
                                disabled={selectedFacts.size === 0}
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                Apply Selected Fixes ({selectedFacts.size})
                              </Button>
                              <Button
                                onClick={() => setShowFactCheck(false)}
                                variant="outline"
                                size="sm"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-3 max-h-[500px] overflow-y-auto">
                            {filteredFacts.map((fact, filteredIndex) => {
                              // Get the original index in factCheckResults for selection tracking
                              const factIndex = factCheckResults.indexOf(fact);
                              return (
                                <Card
                                  key={factIndex}
                                  className={cn(
                                    "transition-all duration-200 cursor-pointer border-l-4",
                                    getFactCardClass(fact),
                                    selectedFacts.has(factIndex) && "ring-2 ring-primary"
                                  )}
                                  onClick={() => toggleFactSelection(factIndex)}
                                >
                                  <CardContent className="p-4">
                                    <div className="space-y-3">
                                      {/* Statement with checkbox */}
                                      <div className="flex items-start gap-3">
                                        <input
                                          type="checkbox"
                                          checked={selectedFacts.has(factIndex)}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleFactSelection(factIndex);
                                          }}
                                          className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <div className="flex-1 space-y-2">
                                          <div className="flex items-start gap-2">
                                            {getFactStatusIcon(fact)}
                                            <p className="font-medium text-foreground leading-relaxed">
                                              {fact.Statement}
                                            </p>
                                          </div>
                                          
                                          {/* Status and counts */}
                                          <div className="flex items-center gap-4 text-sm">
                                            <span className={cn(
                                              "font-semibold",
                                              fact.Yes_Count === 3 && "text-green-600",
                                              fact.Yes_Count === 0 && fact.No_Count > 0 && "text-red-600",
                                              fact.Yes_Count > 0 && fact.No_Count > 0 && "text-yellow-600"
                                            )}>
                                              {getFactStatusText(fact)}
                                            </span>
                                            <span className="text-muted-foreground">
                                              ✓ {fact.Yes_Count} verified • ✗ {fact.No_Count} disputed
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Suggestions for disputed facts */}
                                      {fact.No_Sources && fact.No_Sources.length > 0 && (
                                        <div className="bg-red-50/50 dark:bg-red-950/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
                                          <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                                            Disputed Sources & Suggestions:
                                          </h4>
                                          <div className="space-y-2">
                                            {fact.No_Sources.map((source, i) => (
                                              <div key={i} className="text-sm space-y-1">
                                                <div className="text-red-700 dark:text-red-300">
                                                  <strong>{source.source}:</strong> {source.reason}
                                                </div>
                                                {source.suggestion && (
                                                  <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded p-2">
                                                    <span className="text-xs font-medium text-green-700 dark:text-green-300 uppercase tracking-wide">
                                                      Suggested Fix:
                                                    </span>
                                                    <p className="text-green-800 dark:text-green-200 mt-1">
                                                      {source.suggestion}
                                                    </p>
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}
                  </motion.div>
                )}
              </div>

              {/* Article Editing Chat Interface */}
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-2 p-4 bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg border">
                  <MessageSquare className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold">Edit Article</h3>
                </div>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="edit-chat">Ask for article modifications</Label>
                          <Textarea
                            id="edit-chat"
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            placeholder="e.g., 'Add more details about pricing', 'Make the introduction more engaging', 'Include more technical specifications'..."
                            className="min-h-[100px] resize-none"
                            disabled={isEditingArticle}
                          />
                        </div>
                        
                        <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-lg">
                          <input
                            type="checkbox"
                            id="web-search-toggle"
                            checked={useWebSearch}
                            onChange={(e) => setUseWebSearch(e.target.checked)}
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                          />
                          <Label htmlFor="web-search-toggle" className="text-sm font-medium">
                            Enable web search for updated information
                          </Label>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={editArticle}
                          disabled={isEditingArticle || !chatInput.trim()}
                          className="flex-1"
                        >
                          {isEditingArticle ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                              Editing Article...
                            </>
                          ) : (
                            <>
                              <MessageSquare className="h-4 w-4 mr-2" />
                              Edit Article
                            </>
                          )}
                        </Button>
                        
                        <Button
                          variant="outline"
                          onClick={() => setChatInput('')}
                          disabled={isEditingArticle}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

            </div>
          )}
        </div>
      </div>
    );
};

export default StartupStashSection;